import api from './client';

async function downloadAdminFile(path, fallbackFilename) {
  const res = await api.get(path, { responseType: 'blob' });
  const blob = res.data;
  const ctype = (res.headers['content-type'] || '').toLowerCase();

  // API-fel kommer ofta som JSON även vid responseType blob — då ska vi inte spara en "fil"
  if (ctype.includes('application/json')) {
    const text = await blob.text();
    let detail = 'Kunde inte ladda ner filen';
    try {
      const j = JSON.parse(text);
      detail = typeof j.detail === 'string' ? j.detail : j.message || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }

  const cd = res.headers['content-disposition'];
  let filename = fallbackFilename;
  if (cd) {
    const star = /filename\*=UTF-8''([^;\s]+)/i.exec(cd);
    const plain = /filename="([^"]+)"/i.exec(cd) || /filename=([^;\s]+)/i.exec(cd);
    if (star) {
      try {
        filename = decodeURIComponent(star[1].trim());
      } catch {
        /* ignore */
      }
    } else if (plain) {
      filename = plain[1].trim().replace(/^["']|["']$/g, '');
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || fallbackFilename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Kort fördröjning så webbläsaren hinner starta nedladdning till disk (Nedladdningar etc.)
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export const adminApi = {
  getStatistics: () => api.get('/admin/statistics'),
  getStores: (params = {}) => api.get('/admin/stores', { params }),
  updateStore: (storeId, data) => api.put(`/admin/stores/${storeId}`, data),
  toggleStorePause: (storeId) => api.put(`/admin/stores/${storeId}/pause`),
  getProducts: (params = {}) => api.get('/admin/products', { params }),
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (productId, data) => api.put(`/admin/products/${productId}`, data),
  deleteProduct: (productId) => api.delete(`/admin/products/${productId}`),
  getByggfyndProducts: (params = {}) => api.get('/admin/byggfynd-products', { params }),
  getOrders: (params = {}) => api.get('/admin/orders', { params }),
  toggleUserPause: (userId) => api.put(`/admin/users/${userId}/pause`),
  getBanners: (params = {}) => api.get('/admin/banners', { params }),
  createBanner: (data) => api.post('/admin/banners', data),
  updateBannerStatus: (bannerId, status) => api.put(`/admin/banners/${bannerId}/status`, { status }),
  updateBannerPayment: (bannerId, paymentStatus) => api.put(`/admin/banners/${bannerId}/payment`, { payment_status: paymentStatus }),
  updateBanner: (bannerId, params) => api.put(`/admin/banners/${bannerId}?${params}`),
  deleteBanner: (bannerId) => api.delete(`/admin/banners/${bannerId}`),
  getBannerSettings: () => api.get('/admin/banner-settings'),
  updateBannerSettings: (data) => api.put('/admin/banner-settings', data),
  getPartnerSettings: () => api.get('/admin/partner-settings'),
  updatePartnerSettings: (data) => api.put('/admin/partner-settings', data),
  createPartnerStore: (data) => api.post('/admin/partner/stores', data),
  getShippingSettings: () => api.get('/admin/shipping-settings'),
  updateShippingSettings: (data) => api.put('/admin/shipping-settings', data),
  getLegalTerms: () => api.get('/admin/legal-terms'),
  updateLegalTerms: (data) => api.put('/admin/legal-terms', data),
  getCustomers: (params = {}) => api.get('/admin/customers', { params }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  deleteStore: (storeId) => api.delete(`/admin/stores/${storeId}`),
  getShippingLabel: (orderId, storeId) => api.get(`/admin/orders/${orderId}/shipping-label${storeId ? `?store_id=${storeId}` : ''}`),
  getInvoiceData: (startDate, endDate) => api.get(`/admin/invoice-data?start_date=${startDate}&end_date=${endDate}`),
  getInvoiceStoreDetail: (storeId, startDate, endDate) => api.get(`/admin/invoice-data/${storeId}?start_date=${startDate}&end_date=${endDate}`),
  getInvoiceOrderContext: (orderId) =>
    api.get(`/admin/invoice-data/order/${encodeURIComponent(orderId)}`),
  getInvoicePeriodOrders: (startDate, endDate) =>
    api.get(
      `/admin/invoice-data/period-orders?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
    ),
  searchInvoiceOrders: (q) =>
    api.get(`/admin/invoice-data/order-search?q=${encodeURIComponent(q)}`),
  downloadInvoiceFortnoxCsv: (startDate, endDate) =>
    downloadAdminFile(
      `/admin/invoice-export/fortnox?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
      'byggfynd-fortnox.csv',
    ),
  downloadInvoiceFortnoxCsvForStore: (storeId, startDate, endDate) =>
    downloadAdminFile(
      `/admin/invoice-export/fortnox/${encodeURIComponent(storeId)}?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
      'fortnox-butik.csv',
    ),
  downloadInvoiceUtbetalningAllCsv: (startDate, endDate) =>
    downloadAdminFile(
      `/admin/invoice-export/utbetalning-all?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
      'utbetalning-alla.csv',
    ),
  downloadInvoiceOverviewCsv: (startDate, endDate) =>
    downloadAdminFile(
      `/admin/invoice-export/overview?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
      'fakturaunderlag-oversikt.csv',
    ),
  downloadInvoiceUtbetalningCsv: (storeId, startDate, endDate) =>
    downloadAdminFile(
      `/admin/invoice-export/utbetalning/${encodeURIComponent(storeId)}?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
      'utbetalning.csv',
    ),
  downloadInvoiceUtbetalningPdf: (storeId, startDate, endDate) =>
    downloadAdminFile(
      `/admin/invoice-export/utbetalning/${encodeURIComponent(storeId)}/pdf?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
      'utbetalning.pdf',
    ),
  getPayoutQueue: (startDate, endDate) =>
    api.get(
      `/admin/payout-queue?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
    ),
  getPayoutQueueDetail: (storeId, startDate, endDate) =>
    api.get(
      `/admin/payout-queue/${encodeURIComponent(storeId)}?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
    ),
  markPayoutPaid: (storeId, startDate, endDate) =>
    api.post('/admin/payout-queue/mark-paid', {
      store_id: storeId,
      start_date: startDate,
      end_date: endDate,
    }),
  downloadPayoutFortnoxCsv: (startDate, endDate) =>
    downloadAdminFile(
      `/admin/invoice-export/fortnox?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}&unpaid_only=true`,
      'byggfynd-fortnox-obetalda.csv',
    ),
  downloadPayoutUtbetalningAllCsv: (startDate, endDate) =>
    downloadAdminFile(
      `/admin/invoice-export/utbetalning-all?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}&unpaid_only=true`,
      'utbetalning-alla-obetalda.csv',
    ),
  downloadPayoutUtbetalningCsv: (storeId, startDate, endDate) =>
    downloadAdminFile(
      `/admin/invoice-export/utbetalning/${encodeURIComponent(storeId)}?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}&unpaid_only=true`,
      'utbetalning-obetalda.csv',
    ),
  downloadPayoutUtbetalningPdf: (storeId, startDate, endDate) =>
    downloadAdminFile(
      `/admin/invoice-export/utbetalning/${encodeURIComponent(storeId)}/pdf?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}&unpaid_only=true`,
      'utbetalning-obetalda.pdf',
    ),
};
